import { Avatar, AvatarFallback } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Building, Check, Loader2, Mail, Plus, RefreshCw, Send, User, X } from 'lucide-react';
import React, { useCallback, useEffect, useState } from 'react';
import { useToast } from '../../hooks/use-toast';
import {
  cancelInvite,
  getPendingInvites,
  getSentInvites,
  respondToInvite,
  type PendingInvite,
  type SentInvite,
} from '../../services/inviteService';
import { SendInviteDialog } from '../invitation/SendInviteDialog';

type Tab = 'received' | 'sent';

const STATUS_BADGE: Record<
  SentInvite['receiver_join_status'],
  { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }
> = {
  pending:  { label: 'Pending',  variant: 'secondary' },
  joined:   { label: 'Accepted', variant: 'default' },
  declined: { label: 'Declined', variant: 'destructive' },
};

export const InvitationsSettingsTab: React.FC = () => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<Tab>('received');

  // Received state
  const [received, setReceived] = useState<PendingInvite[]>([]);
  const [loadingReceived, setLoadingReceived] = useState(true);
  const [respondingTo, setRespondingTo] = useState<string | null>(null);

  // Sent state
  const [sent, setSent] = useState<SentInvite[]>([]);
  const [loadingSent, setLoadingSent] = useState(false);
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  // Invite dialog state
  const [inviteOpen, setInviteOpen] = useState(false);

  const loadReceived = useCallback(async () => {
    try {
      setLoadingReceived(true);
      const data = await getPendingInvites();
      setReceived(data);
    } catch (error) {
      console.error('Failed to load received invitations:', error);
      toast({ title: 'Error', description: 'Failed to load received invitations.', variant: 'destructive' });
    } finally {
      setLoadingReceived(false);
    }
  }, [toast]);

  const loadSent = useCallback(async () => {
    try {
      setLoadingSent(true);
      const data = await getSentInvites();
      setSent(data);
    } catch (error) {
      console.error('Failed to load sent invitations:', error);
      toast({ title: 'Error', description: 'Failed to load sent invitations.', variant: 'destructive' });
    } finally {
      setLoadingSent(false);
    }
  }, [toast]);

  useEffect(() => { loadReceived(); }, [loadReceived]);

  useEffect(() => {
    if (activeTab === 'sent') loadSent();
  }, [activeTab, loadSent]);

  const handleRespond = async (invite: PendingInvite, action: 'accept' | 'decline') => {
    try {
      setRespondingTo(invite.id);
      await respondToInvite(invite.invite_code, action);
      toast({
        title: action === 'accept' ? 'Invite accepted' : 'Invite declined',
        description:
          action === 'accept'
            ? invite.invite_type === 'organisation'
              ? `You joined ${invite.tenant_name ?? 'the organisation'}.`
              : 'Personal invite accepted.'
            : 'Invitation declined.',
      });
      setReceived(prev => prev.filter(i => i.id !== invite.id));
    } catch (error) {
      console.error('Failed to respond to invite:', error);
      toast({ title: 'Error', description: 'Failed to respond to invitation. Please try again.', variant: 'destructive' });
    } finally {
      setRespondingTo(null);
    }
  };

  const handleCancel = async (invite: SentInvite) => {
    try {
      setCancellingId(invite.id);
      await cancelInvite(invite.id);
      toast({ title: 'Invitation cancelled', description: `Invitation to ${invite.receiver_email} has been cancelled.` });
      setSent(prev => prev.filter(i => i.id !== invite.id));
    } catch (error) {
      console.error('Failed to cancel invite:', error);
      toast({ title: 'Error', description: 'Failed to cancel invitation. Please try again.', variant: 'destructive' });
    } finally {
      setCancellingId(null);
    }
  };

  const handleRefresh = () => {
    if (activeTab === 'received') loadReceived();
    else loadSent();
  };

  const isLoading = activeTab === 'received' ? loadingReceived : loadingSent;

  return (
    <div className="dashboard-card p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-semibold">Invitations</h3>
          <p className="text-sm text-muted-foreground mt-1">
            Manage invitations sent to you and sent by you
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => setInviteOpen(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Invite
          </Button>
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isLoading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-muted rounded-lg w-fit">
        <button
          onClick={() => setActiveTab('received')}
          className={`flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'received'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <Mail className="w-3.5 h-3.5" />
          Received
          {received.length > 0 && (
            <span className="ml-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-bold text-primary-foreground">
              {received.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setActiveTab('sent')}
          className={`flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
            activeTab === 'sent'
              ? 'bg-background text-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <Send className="w-3.5 h-3.5" />
          Sent
        </button>
      </div>

      {/* Received Tab */}
      {activeTab === 'received' && (
        <>
          {loadingReceived ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : received.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Mail className="w-12 h-12 mx-auto mb-4 opacity-40" />
              <p className="font-medium">No pending invitations</p>
              <p className="text-sm mt-1">When someone invites you, it will appear here.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {received.map(invite => {
                const isOrg = invite.invite_type === 'organisation';
                const isBusy = respondingTo === invite.id;
                return (
                  <div key={invite.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <Avatar>
                        <AvatarFallback>
                          {isOrg ? <Building className="w-4 h-4" /> : <User className="w-4 h-4" />}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-sm">
                          {isOrg ? invite.tenant_name ?? 'Organisation invite' : 'Personal invite'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          From {invite.sender_name} · {new Date(invite.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <Badge variant={isOrg ? 'default' : 'secondary'} className="ml-2">
                        {isOrg ? 'Organisation' : 'Personal'}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button size="sm" variant="outline" onClick={() => handleRespond(invite, 'decline')} disabled={isBusy}>
                        {isBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Decline'}
                      </Button>
                      <Button size="sm" onClick={() => handleRespond(invite, 'accept')} disabled={isBusy}>
                        {isBusy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                        Accept
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Sent Tab */}
      {activeTab === 'sent' && (
        <>
          {loadingSent ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : sent.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Send className="w-12 h-12 mx-auto mb-4 opacity-40" />
              <p className="font-medium">No sent invitations</p>
              <p className="text-sm mt-1">Invitations you send will appear here.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {sent.map(invite => {
                const isOrg = invite.invite_type === 'organisation';
                const isPending = invite.receiver_join_status === 'pending';
                const isCancelling = cancellingId === invite.id;
                const statusCfg = STATUS_BADGE[invite.receiver_join_status];
                return (
                  <div key={invite.id} className="flex items-center justify-between p-4 border rounded-lg">
                    <div className="flex items-center gap-3">
                      <Avatar>
                        <AvatarFallback>
                          {isOrg ? <Building className="w-4 h-4" /> : <User className="w-4 h-4" />}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-sm">{invite.receiver_email}</p>
                        <p className="text-xs text-muted-foreground">
                          {isOrg && invite.tenant_name ? `${invite.tenant_name} · ` : ''}
                          {invite.role_name ? `Role: ${invite.role_name} · ` : ''}
                          {new Date(invite.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 ml-2">
                        <Badge variant={isOrg ? 'default' : 'secondary'}>
                          {isOrg ? 'Organisation' : 'Personal'}
                        </Badge>
                        <Badge variant={statusCfg.variant} className="flex items-center gap-1">
                          {invite.receiver_join_status === 'joined' && <Check className="w-3 h-3" />}
                          {statusCfg.label}
                        </Badge>
                      </div>
                    </div>
                    {isPending && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleCancel(invite)}
                        disabled={isCancelling}
                        className="text-destructive hover:text-destructive"
                      >
                        {isCancelling ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <><X className="w-3 h-3 mr-1" />Cancel</>
                        )}
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      <SendInviteDialog
        open={inviteOpen}
        onOpenChange={setInviteOpen}
        onInvitesSent={loadSent}
      />
    </div>
  );
};
